import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Tag, Clock, RefreshCw, Check, X, Copy,
  AlertTriangle, Loader2,
} from "lucide-react";

const fmtRedeemBy = (ts) => {
  if (!ts) return "Sem validade";
  const d = new Date(Number(ts) * 1000);
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

const CouponRow = ({ coupon, onDelete }) => {
  const expired = coupon.redeem_by && (coupon.redeem_by * 1000) < Date.now();
  const exhausted =
    coupon.max_redemptions != null && coupon.times_redeemed >= coupon.max_redemptions;
  const inactive = !coupon.valid || expired || exhausted;

  return (
    <div
      className={`rounded-xl border p-5 transition-colors ${
        inactive
          ? "bg-[#0C0E12] border-white/5 opacity-60"
          : "bg-[#161920] border-white/10 hover:border-[#C5A880]/50"
      }`}
      data-testid={`coupon-${coupon.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-[#C5A880]" />
            <h3 className="font-display text-xl text-[#F5F2EE] truncate" data-testid={`coupon-code-${coupon.id}`}>
              {coupon.promo_code || coupon.id}
            </h3>
            <button
              onClick={() => {
                navigator.clipboard.writeText(coupon.promo_code || coupon.id);
                toast.success("Copiado.");
              }}
              title="Copiar código"
              className="text-[#A29F9B] hover:text-[#C5A880]"
              data-testid={`coupon-copy-${coupon.id}`}
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          {coupon.name && <p className="text-xs text-[#A29F9B] mt-1">{coupon.name}</p>}
        </div>
        <div className="text-right">
          <div className="font-display text-2xl text-[#C5A880]" data-testid={`coupon-discount-${coupon.id}`}>
            {coupon.percent_off != null
              ? `-${coupon.percent_off}%`
              : `-${(coupon.amount_off || 0).toFixed(2)}€`}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#A29F9B] mt-0.5">
            {coupon.duration === "once"
              ? "1ª cobrança"
              : coupon.duration === "forever"
              ? "Sempre"
              : `${coupon.duration_in_months ?? "?"} meses`}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 text-xs flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-[#A29F9B]">
          <Clock className="w-3 h-3" /> {fmtRedeemBy(coupon.redeem_by)}
        </span>
        <span className={`inline-flex items-center gap-1.5 ${exhausted ? "text-rose-400" : "text-[#A29F9B]"}`}>
          {coupon.times_redeemed ?? 0}
          {coupon.max_redemptions != null ? ` / ${coupon.max_redemptions}` : " usos"}
        </span>
        {inactive ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-300 text-[10px] uppercase tracking-wider">
            <AlertTriangle className="w-3 h-3" />
            {expired ? "Expirado" : exhausted ? "Esgotado" : "Inativo"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-wider">
            <Check className="w-3 h-3" /> Activo
          </span>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 mt-4 pt-4 border-t border-white/5">
        <button
          onClick={() => onDelete(coupon.id, coupon.promo_code)}
          className="inline-flex items-center gap-1.5 text-xs text-rose-300 hover:text-rose-200"
          data-testid={`coupon-delete-${coupon.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" /> Apagar
        </button>
      </div>
    </div>
  );
};

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    discount_type: "percent",
    percent_off: 20,
    amount_off: 1.0,
    duration: "once",
    duration_in_months: 3,
    max_redemptions: "",
    redeem_by_days: 30,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/coupons");
      setCoupons(r.data?.coupons || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao obter cupões.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.code || form.code.trim().length < 3) {
      toast.error("Código com pelo menos 3 caracteres.");
      return;
    }
    setCreating(true);
    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim() || null,
      duration: form.duration,
    };
    if (form.discount_type === "percent") {
      payload.percent_off = Number(form.percent_off);
    } else {
      payload.amount_off = Number(form.amount_off);
    }
    if (form.duration === "repeating") {
      payload.duration_in_months = Number(form.duration_in_months) || 3;
    }
    if (form.max_redemptions) payload.max_redemptions = Number(form.max_redemptions);
    if (form.redeem_by_days) payload.redeem_by_days = Number(form.redeem_by_days);

    try {
      await client.post("/admin/coupons", payload);
      toast.success(`Cupão ${payload.code} criado.`);
      setForm((f) => ({ ...f, code: "", name: "" }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar cupão.");
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id, promoCode) => {
    if (!window.confirm(`Apagar cupão ${promoCode || id}? Esta acção é permanente.`)) return;
    try {
      await client.delete(`/admin/coupons/${encodeURIComponent(id)}`);
      toast.success("Cupão apagado.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao apagar.");
    }
  };

  return (
    <div className="min-h-screen bg-[#0C0E12] text-[#F5F2EE]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <Link to="/admin"
          className="inline-flex items-center gap-2 text-sm text-[#A29F9B] hover:text-[#C5A880]"
          data-testid="back-to-admin">
          <ArrowLeft className="w-4 h-4" /> Voltar ao Admin
        </Link>

        <header>
          <span className="label-uppercase">Gestão Stripe</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Cupões de desconto</h1>
          <p className="text-[#A29F9B] text-sm mt-2 max-w-2xl">
            Os cupões são criados directamente no Stripe (LIVE) e ficam disponíveis
            para os utilizadores aplicarem no checkout ou na página Premium. Cada
            cupão gera um Promotion Code com o mesmo nome, já utilizável de imediato.
          </p>
        </header>

        {/* Create form */}
        <section className="bg-[#161920] border border-white/10 rounded-2xl p-6 md:p-8" data-testid="coupon-create-section">
          <h2 className="font-display text-2xl mb-4 inline-flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#C5A880]" /> Criar novo cupão
          </h2>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#A29F9B] mb-2">Código</label>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="ex.: VERAO25"
                className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm uppercase"
                data-testid="coupon-form-code"
                maxLength={40}
                required
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#A29F9B] mb-2">Descrição interna (opcional)</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ex.: Campanha Verão 2026"
                className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="coupon-form-name"
                maxLength={40}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#A29F9B] mb-2">Tipo de desconto</label>
              <div className="inline-flex rounded-lg bg-[#0C0E12] border border-white/10 p-1">
                <button type="button"
                  onClick={() => setForm({ ...form, discount_type: "percent" })}
                  className={`px-3 py-1 rounded-md text-xs transition ${
                    form.discount_type === "percent"
                      ? "bg-[#C5A880] text-[#0C0E12] font-semibold"
                      : "text-[#A29F9B] hover:text-[#F5F2EE]"
                  }`}
                  data-testid="coupon-form-type-percent">
                  Percentagem
                </button>
                <button type="button"
                  onClick={() => setForm({ ...form, discount_type: "amount" })}
                  className={`px-3 py-1 rounded-md text-xs transition ${
                    form.discount_type === "amount"
                      ? "bg-[#C5A880] text-[#0C0E12] font-semibold"
                      : "text-[#A29F9B] hover:text-[#F5F2EE]"
                  }`}
                  data-testid="coupon-form-type-amount">
                  Valor fixo (€)
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#A29F9B] mb-2">
                {form.discount_type === "percent" ? "% off" : "€ off"}
              </label>
              <input
                type="number"
                step={form.discount_type === "percent" ? "1" : "0.01"}
                min={form.discount_type === "percent" ? 1 : 0.01}
                max={form.discount_type === "percent" ? 100 : undefined}
                value={form.discount_type === "percent" ? form.percent_off : form.amount_off}
                onChange={(e) => setForm({
                  ...form,
                  [form.discount_type === "percent" ? "percent_off" : "amount_off"]:
                    e.target.value,
                })}
                className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="coupon-form-value"
                required
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#A29F9B] mb-2">Aplicação</label>
              <select
                value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })}
                className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="coupon-form-duration"
              >
                <option value="once">Só na 1ª cobrança</option>
                <option value="repeating">Durante N meses</option>
                <option value="forever">Para sempre (todas as renovações)</option>
              </select>
            </div>
            {form.duration === "repeating" && (
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#A29F9B] mb-2">Nº meses</label>
                <input
                  type="number" min={1} max={48}
                  value={form.duration_in_months}
                  onChange={(e) => setForm({ ...form, duration_in_months: e.target.value })}
                  className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm"
                  data-testid="coupon-form-duration-months"
                />
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#A29F9B] mb-2">
                Nº máximo de resgates <span className="normal-case text-[#A29F9B]/70">(vazio = ilimitado)</span>
              </label>
              <input
                type="number" min={1} max={10000}
                value={form.max_redemptions}
                onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                placeholder="ex.: 100"
                className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="coupon-form-max-redemptions"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#A29F9B] mb-2">
                Expira em (dias) <span className="normal-case text-[#A29F9B]/70">(vazio = sem expiração)</span>
              </label>
              <input
                type="number" min={1} max={365}
                value={form.redeem_by_days}
                onChange={(e) => setForm({ ...form, redeem_by_days: e.target.value })}
                placeholder="ex.: 30"
                className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm"
                data-testid="coupon-form-redeem-days"
              />
            </div>

            <div className="md:col-span-2 flex justify-end pt-2">
              <button type="submit" disabled={creating}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="coupon-form-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {creating ? "A criar..." : "Criar cupão"}
              </button>
            </div>
          </form>
        </section>

        {/* List */}
        <section data-testid="coupon-list-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl inline-flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#C5A880]" /> Cupões existentes
            </h2>
            <button onClick={load} disabled={loading}
              className="inline-flex items-center gap-1.5 text-sm text-[#A29F9B] hover:text-[#C5A880]"
              data-testid="coupon-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </button>
          </div>
          {loading ? (
            <p className="text-[#A29F9B] text-sm">A carregar...</p>
          ) : coupons.length === 0 ? (
            <div className="bg-[#161920] border border-white/5 rounded-xl p-8 text-center">
              <X className="w-8 h-8 text-[#A29F9B]/50 mx-auto mb-3" />
              <p className="text-[#A29F9B] text-sm">Ainda não tens cupões ativos.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="coupon-list">
              {coupons.map((c) => (
                <CouponRow key={c.id} coupon={c} onDelete={onDelete} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AdminCoupons;
